import type {
  Actor,
  ApplicationStatus,
  ConsentChannel,
  ConsentDocument,
  PaymentStatus,
  PublicSettings,
  AppSettings,
  UserRole,
  WorkerHealth,
} from '@getslot/domain'

// Ответы API, пока нет OpenAPI. Источник правды — контроллеры apps/api/src/modules/*.

/** Тело любой ошибки API (AllExceptionsFilter). */
export interface ErrorBody {
  code: string
  message: string
  details?: unknown
}

/** POST /auth/telegram, POST /auth/refresh */
export interface TokenResponse {
  access_token: string
  refresh_token: string
  token_type: 'Bearer'
  expires_in: number
}

/** GET /me */
export interface MeResponse {
  id: string
  role: UserRole
  display_name: string | null
}

/** GET /me/consents, POST /me/consents */
export interface ConsentsResponse {
  documents: Array<{ document: ConsentDocument; version: string; accepted_at: string | null }>
  /** Всё принято в действующей редакции: можно создавать заявку. */
  complete: boolean
}

/** POST /me/consents */
export interface AcceptConsentsBody {
  documents: Array<{ document: ConsentDocument; version: string }>
  channel: ConsentChannel
}

/** Коды ошибок входа, на которые интерфейс отвечает своим экраном. */
export const AUTH_ERROR_CODES = {
  betaClosed: 'beta_closed',
} as const

/** Коды ошибок заявок, на которые интерфейс отвечает по-своему. */
export const APPLICATION_ERROR_CODES = {
  versionConflict: 'version_conflict',
  datesInvalid: 'dates_invalid',
  notEditable: 'application_not_editable',
  credentialsRequired: 'credentials_required',
} as const

export interface DateRangeDto {
  from: string
  to: string
}

/** GET /applications/:id без таймлайна, элемент списка, снимок в SSE. */
export interface ApplicationDto {
  id: string
  post: string
  visa_category: string
  date_ranges: DateRangeDto[]
  excluded_dates: string[]
  min_lead_days: number
  status: ApplicationStatus
  status_reason: string | null
  status_changed_at: string
  /** Время собеседования из кабинета, после «Записан». */
  appointment_at?: string | null
  version: number
  created_at: string
  updated_at: string
}

/** Событие таймлайна: без секретов и без id того, кто его вызвал. */
export interface TimelineEventDto {
  id: string
  kind: string
  from_status: ApplicationStatus | null
  to_status: ApplicationStatus | null
  actor: Actor
  reason: string | null
  payload: Record<string, unknown>
  created_at: string
}

/** Доступ к кабинету: только факт и время, логин и пароль API не отдаёт. */
export interface AccessDto {
  saved: boolean
  saved_at: string | null
  verified_at: string | null
}

/** Оплата заявки. Постоплата: реквизиты приходят только после записи и пока не оплачено. */
export interface PaymentDto {
  status: PaymentStatus
  amount_rub: number
  /** Запись подтверждена, пора платить. */
  due: boolean
  paid_at: string | null
  /** «На тестировании постоплата, позже привязка карты». */
  note: string
  /** Реквизиты для перевода; `null` — ещё не время, уже оплачено или реквизиты не заполнены. */
  transfer_details: string | null
}

export interface ApplicationDetailDto extends ApplicationDto {
  timeline: TimelineEventDto[]
  access: AccessDto
  payment: PaymentDto
}

export interface CredentialsBody {
  login: string
  password: string
}

export interface Page<T> {
  items: T[]
  total: number
  limit: number
  offset: number
}

/** POST /applications (заголовок Idempotency-Key обязателен). */
export interface CreateApplicationBody {
  post: string
  visa_category?: string
  date_ranges?: DateRangeDto[]
  excluded_dates?: string[]
  min_lead_days?: number
}

/** PATCH /applications/:id: `version` из последнего чтения. */
export interface UpdateApplicationBody extends Partial<CreateApplicationBody> {
  version: number
}

/** POST /auth/telegram-widget: поля Telegram Login Widget как есть. */
export interface TelegramWidgetUser {
  id: number
  first_name: string
  last_name?: string
  username?: string
  photo_url?: string
  auth_date: number
  hash: string
}

/** SSE /applications/:id/stream */
export type ApplicationStreamMessage =
  { type: 'application_event'; event: TimelineEventDto } | { type: 'application'; application: ApplicationDto }

/** Админка: заявка с клиентом. */
export interface AdminClientBrief {
  id: string
  display_name: string | null
  /** Ник Telegram без @. */
  username: string | null
  telegram_id: string | null
  photo_url: string | null
}

export interface AdminApplicationDto extends ApplicationDto {
  client: AdminClientBrief
  /** Оператор, который ведёт заявку. */
  assignee: { id: string; display_name: string | null } | null
  last_polled_at: string | null
  payment: AdminPaymentDto
}

/** Оплата в админке: кто и когда отметил «оплачено». */
export interface AdminPaymentDto {
  status: PaymentStatus
  amount_rub: number
  due: boolean
  paid_at: string | null
  marked_by: { id: string; display_name: string | null } | null
}

export interface AdminApplicationList extends Page<AdminApplicationDto> {
  /** Счётчики по всем статусам без фильтра: для вкладок. */
  counts: Partial<Record<ApplicationStatus, number>>
}

export interface AdminCardDto extends AdminApplicationDto {
  allowed_transitions: ApplicationStatus[]
  timeline: (TimelineEventDto & { actor_user_id: string | null })[]
}

export interface AdminTransitionBody {
  to: ApplicationStatus
  reason?: string
  /** Время собеседования из кабинета, ISO; обязательно для «Записан». */
  appointment_at?: string
  version?: number
}

export interface AdminAssignBody {
  /** `null` снимает ответственного. */
  user_id: string | null
}

/** Клиент в админке: профиль из Telegram, контакты, статусы всех заявок. */
export interface AdminClientDto {
  id: string
  role: UserRole
  display_name: string | null
  username: string | null
  first_name: string | null
  last_name: string | null
  language_code: string | null
  photo_url: string | null
  is_premium: boolean
  telegram_id: string | null
  /** false, если клиент заблокировал бота. */
  can_message: boolean
  phone: string | null
  email: string | null
  created_at: string
  last_seen_at: string | null
  applications: { id: string; status: ApplicationStatus }[]
}

export type AdminClientList = Page<AdminClientDto>

export interface AdminClientCardDto extends AdminClientDto {
  staff_note: string | null
  application_items: AdminApplicationDto[]
  timeline: (TimelineEventDto & { application_id: string })[]
}

export interface AdminClientPatch {
  phone?: string | null
  email?: string | null
  staff_note?: string | null
}

export interface AdminWorkerDto {
  id: string
  health: WorkerHealth
  region: string
  instance: string
  posts: string[]
  adapter: string
  version: string
  current_job_kind: string | null
  current_application_id: string | null
  started_at: string
  last_beat_at: string
  last_cycle_at: string | null
  last_cycle_ms: number | null
  /** Только код ошибки, без текста ответа портала. */
  last_error: string | null
  last_error_at: string | null
  /** Несекретные настройки процесса: интервалы, режим, лимиты. */
  config: Record<string, unknown>
}

export interface AdminWorkers {
  workers: AdminWorkerDto[]
  jobs: {
    kind: string
    label: string
    waiting: number
    running: number
    failed_24h: number
    oldest_due_at: string | null
  }[]
  errors: {
    id: string
    kind: string
    label: string
    application_id: string | null
    locked_by: string | null
    last_error: string
    at: string
  }[]
}

export interface AdminSummary {
  counts: Partial<Record<ApplicationStatus, number>>
  found_24h: number
  booked_7d: number
  clients: number
  /** Заявки, которые ждут оператора, клиента или проверки найденной даты; старые первыми. */
  attention: AdminApplicationDto[]
  feed: (TimelineEventDto & { application_id: string; client_name: string | null })[]
  workers: AdminWorkerDto[]
  staff: { id: string; display_name: string | null; role: UserRole }[]
}

/** Админка: пользователь со своей ролью. Только для роли admin. */
export interface AdminUserDto {
  id: string
  role: UserRole
  display_name: string | null
  telegram_username: string | null
  telegram_user_id: number | null
  created_at: string
  /** Последний вход через Telegram. */
  last_seen_at: string | null
}

export type AdminUserList = Page<AdminUserDto>

/** PATCH /admin/users/:id/role. Себе роль сменить нельзя (`own_role_locked`). */
export interface AdminSetRoleBody {
  role: UserRole
}

export interface BetaInviteDto {
  telegram_user_id: number
  note: string | null
  invited_by_name: string | null
  /** Уже входил в приложение. */
  joined: boolean
  created_at: string
}

/** GET /admin/beta: список из окружения только для чтения, приглашения из админки. */
export interface BetaStateDto {
  closed: boolean
  env_ids: number[]
  invites: BetaInviteDto[]
}

/** POST /admin/beta/invites */
export interface BetaInviteBody {
  telegram_user_id: number
  note?: string
}

export const ADMIN_USERS_ERROR_CODES = {
  ownRoleLocked: 'own_role_locked',
  inviteExists: 'beta_invite_exists',
} as const

/** GET /settings: цена, срок оплаты и реквизиты исполнителя. Публичный, без реквизитов перевода. */
export type PublicSettingsDto = PublicSettings

/** GET, PATCH /admin/settings: только админ. `transfer_details_source` — откуда реквизиты перевода. */
export interface AdminSettingsDto extends AppSettings {
  transfer_details_source: 'admin' | 'env' | null
}

/** PATCH /admin/settings: только изменённые поля; `null` или пустая строка возвращают значение по умолчанию. */
export type UpdateSettingsBody = Partial<{ [K in keyof AppSettings]: AppSettings[K] | null }>
